import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

interface FeedbackPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (feedback: string, canContact: boolean, rating: string) => Promise<boolean>;
}

export const FeedbackPopup: React.FC<FeedbackPopupProps> = ({ 
  open, 
  onOpenChange, 
  onSubmit 
}) => {
  const [feedback, setFeedback] = useState('');
  const [canContact, setCanContact] = useState(false);
  const [rating, setRating] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [ratingError, setRatingError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!feedback.trim() || !rating) {
      if (!rating) setRatingError('Please select a rating before submitting.');
      return;
    }
    setRatingError(null);
    setIsSubmitting(true);
    const success = await onSubmit(feedback, canContact, rating);
    setIsSubmitting(false);

    if (success) {
      setShowThankYou(true);
      setTimeout(() => {
        setShowThankYou(false);
        setFeedback('');
        setRating('');
        setCanContact(false);
        onOpenChange(false);
      }, 5000);
    }
  };

  // Reset form when dialog closes
  const handleDialogChange = (open: boolean) => {
    if (!open) {
      setTimeout(() => {
        setFeedback('');
        setRating('');
        setCanContact(false);
        setShowThankYou(false);
      }, 300);
    }
    onOpenChange(open);
  };

  const ratingOptions = [
    { emoji: '😖', value: 'very_negative', label: 'Bit of a mess' },
    { emoji: '😕', value: 'negative', label: 'Kinda clunky' },
    { emoji: '😐', value: 'neutral', label: "It's alright" },
    { emoji: '🙂', value: 'positive', label: 'Nice and smooth' },
    { emoji: '🤩', value: 'very_positive', label: 'Loved It' }
  ];

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="sm:max-w-[500px] pt-0 pb-8 px-0 bg-gradient-to-br from-[#E8F0F8] to-white shadow-2xl rounded-2xl border-0 animate-fadeInAndScale">
        <div className="relative w-full flex flex-col items-center">
            <div className="w-full rounded-t-2xl bg-gradient-to-r from-[#E8F0F8] via-[#E8F0F8] to-[#E8F0F8] py-6 px-8 flex flex-col items-center shadow-md">
            <DialogTitle className="text-2xl font-bold text-[#333B44] mb-1">Help Us Improve Clou</DialogTitle>
            <DialogDescription className="text-base text-muted-foreground text-center">
              Clou is still in early development, and your feedback really matters to us.<br />
              Please take a moment to let us know what you think!
            </DialogDescription>
          </div>
          <div className="w-full px-8 pb-2">
            {showThankYou ? (
              <div className="flex flex-col items-center justify-center py-12 animate-fadeInAndScale">
                <span className="text-5xl animate-bounce mb-2">💜</span>
                <div className="text-xl font-semibold text-[#333B44] mb-1">Thank you for your feedback!</div>
                <div className="text-sm text-muted-foreground">We appreciate your help in making Clou better.</div>
              </div>
            ) : (
            <>
            <div className="space-y-6 mt-8">
              <Textarea 
                placeholder="Share your thoughts, suggestions, or experiences..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="min-h-[120px] bg-white border border-[#CCCCCC] shadow-sm rounded-lg focus:border-[#3BAFDA] focus:ring-2 focus:ring-[#E8F0F8] transition-all"
              />
              <div className="space-y-2">
                <label className="text-sm font-medium">How would you rate your experience?</label>
                <div className="flex justify-between max-w-[340px] mx-auto gap-2">
                  {ratingOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => { 
                        console.log('Selected rating:', option.value);
                        setRating(option.value); 
                        setRatingError(null); 
                      }}
                      className={`text-4xl p-2 rounded-full transition-all flex flex-col items-center border-2 focus:outline-none focus:ring-2 focus:ring-[#3BAFDA] shadow-sm
                        ${rating === option.value 
                          ? 'bg-gradient-to-br from-[#E8F0F8] to-[#E8F0F8] border-[#3BAFDA] scale-110 shadow-lg' 
                          : 'hover:bg-[#E8F0F8] border-transparent'}
                      `}
                      type="button"
                    >
                      <span className="transition-transform duration-200">{option.emoji}</span>
                      <span className="text-xs mt-1 text-[#5A636E]">{option.label}</span>
                    </button>
                  ))}
                </div>
                {ratingError && (
                  <div className="text-red-600 text-sm mt-2 font-medium animate-fadeIn">{ratingError}</div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="contact-permission"
                  checked={canContact}
                  onCheckedChange={(checked) => setCanContact(!!checked)}
                />
                <label 
                  htmlFor="contact-permission" 
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I'm happy for someone from the Clou team to follow up with me about this.
                </label>
              </div>
              <Button 
                onClick={handleSubmit} 
                disabled={!feedback.trim() || isSubmitting}
                className="w-full bg-gradient-to-r from-[#3BAFDA] to-[#3BAFDA] hover:from-[#2E9AC4] hover:to-[#2E9AC4] text-white font-semibold shadow-lg py-3 text-lg rounded-lg transition-all duration-200"
              >
                {isSubmitting ? "Submitting..." : "Submit Feedback"}
              </Button>
            </div>
            </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
